import { Stack, Typography } from '@mui/joy';
import { useTranslation } from 'react-i18next';

import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { LabeledValue, TextHighlighter } from 'core/elements';
import { ExternalProjectAssociationType } from 'projects/enums';
import { IProjectAutocomplete } from 'projects/types';
import { getActiveExternalProjectAccounts, getExternalProjectNumbers } from 'projects/utils';
import { Color, Level } from 'theme/enums';

interface IProps {
  option: IProjectAutocomplete;
  inputValue?: string;
}

const INLINE_LABEL_OPTIONS = {
  labelLevel: Level.BODY_SM,
  labelSx: { fontWeight: 400 },
  inline: true,
};

export const AlternateProjectOption = ({ option, inputValue }: IProps) => {
  const { t } = useTranslation();

  const billToAccounts = getActiveExternalProjectAccounts(option.externalProjectAccounts).filter(
    (account) => account.associationType === ExternalProjectAssociationType.BILL_TO_ACCOUNT
  );
  const firstAccount = billToAccounts[0];
  const customerName = firstAccount?.customerName || DEFAULT_PLACEHOLDER;
  const accountNumber = firstAccount?.accountNumber || DEFAULT_PLACEHOLDER;
  const additionalAccountsCount = billToAccounts.length - 1;

  const { projectNumber, programNumber } = getExternalProjectNumbers(option);

  return (
    <Stack sx={{ width: '100%' }}>
      <TextHighlighter
        level={Level.TITLE_MD}
        text={option.name || DEFAULT_PLACEHOLDER}
        highlight={inputValue}
      />

      {projectNumber && (
        <LabeledValue
          label={t('labelProjectNumberShort')}
          value={<TextHighlighter text={projectNumber} highlight={inputValue} />}
          options={INLINE_LABEL_OPTIONS}
        />
      )}

      {programNumber && (
        <LabeledValue
          label={t('labelProgramNumberShort')}
          value={<TextHighlighter text={programNumber} highlight={inputValue} />}
          options={INLINE_LABEL_OPTIONS}
        />
      )}

      <LabeledValue
        label={t('labelARNumber')}
        value={<TextHighlighter text={accountNumber} highlight={inputValue} />}
        options={INLINE_LABEL_OPTIONS}
      />

      <LabeledValue
        label={t('labelCustomerName')}
        value={<TextHighlighter text={customerName} highlight={inputValue} />}
        options={INLINE_LABEL_OPTIONS}
      />

      {additionalAccountsCount > 0 && (
        <Typography level={Level.BODY_SM} color={Color.PRIMARY}>
          {t('labelMoreCount', { count: additionalAccountsCount })}
        </Typography>
      )}
    </Stack>
  );
};
